import { type FC } from "react";

import { EvaluationTypeEnum, RecordReviewDisplayState } from "@app_schema";

import { FileManager } from "@styled/file_manager";
import { FileQueue } from "@styled/file_queue";

import { AddRecordsClassifyOverlay } from "./add_records_classify_overlay";
import { CompletedRecordsView } from "./completed_records_view";
import { EvalTypeBadge } from "./eval_type_badge";
import { FollowUpUploadStage } from "./follow_up_upload_stage";
import { type ProcessRecordsStateContext } from "./process_records_context";
import { ProcessingRecordsView } from "./processing_records_view";
import { RecordFilePillStack } from "./records_file_pills";
import {
  RecordsStatusBar,
  RsbChangeLink,
  RsbSpinnerIcon,
  RsbUploadIcon,
} from "./records_status_bar";
import { RecordsUploadZone } from "./records_upload_zone";
import { renderFileQueueItem } from "./shared_card_utils";

export type { ProcessRecordsStateContext } from "./process_records_context";

type FollowUpRecordReview = ProcessRecordsStateContext["recordReview"];

// Per D10: once both zones have files the chrome flips from the upload
// prompt to the active processing bar. Before then, the indigo pre-upload
// chrome stays.
function hasBothFollowUpZones(recordReview: FollowUpRecordReview): boolean {
  if (!recordReview) return false;
  const zone1HasDoc =
    Boolean(recordReview.priorReviewDocument) ||
    (recordReview.baselineSourceDocuments?.length ?? 0) > 0;
  const zone2Count = recordReview.additionalSourceDocuments?.length ?? 0;
  return zone1HasDoc && zone2Count > 0;
}

function followUpProgressPercent(
  recordReview: FollowUpRecordReview,
): number | undefined {
  const total = recordReview?.pagesCount ?? 0;
  if (total <= 0) return undefined;
  const processed = recordReview?.processedPagesCount ?? 0;
  return (processed / total) * 100;
}

// Single-zone upload RSB copy. The *Empty and *Ready server states share the
// same chrome; the pill stack renders only when files have landed, which is
// the sole difference between them.
type UploadPrompt = {
  evalType: EvaluationTypeEnum;
  title: string;
  subtitle: string;
};

const COMPREHENSIVE_UPLOAD: UploadPrompt = {
  evalType: EvaluationTypeEnum.Comprehensive,
  title: "Records",
  subtitle:
    "We'll organize them into a chronological summary — then you can ask the Records Assistant about anything in them.",
};

const SUPPLEMENTAL_UPLOAD: UploadPrompt = {
  evalType: EvaluationTypeEnum.Supplemental,
  title: "Records",
  subtitle:
    "We'll organize them for your review — then you can ask the Records Assistant about anything in them.",
};

// Display-state switch for the Process Records stage. The parent card owns
// mutations + data assembly; this view picks the RSB chrome for each
// server-resolved displayState and delegates terminal + processing states
// to their respective sub-views.

export const ProcessRecordsStateView: FC<{
  context: ProcessRecordsStateContext;
}> = ({ context }) => {
  if (context.overlayOpen) {
    return (
      <AddRecordsClassifyOverlay
        contextPages={context.pages}
        contextLetters={context.letters}
        contextSummary={context.fileSummary}
        evaluationType={context.evaluationType}
        initialKind={context.initialAddRecordsKind}
        onUpload={context.onAddRecordsUpload}
        onDismiss={context.onDismissOverlay}
      />
    );
  }

  switch (context.displayState) {
    // Idle = no evaluation type yet. That state is now owned by the page-level
    // setup gate (EvalTypeGate), which renders before this card mounts — so
    // the card never actually sees Idle. Defensive null keeps the switch total.
    case RecordReviewDisplayState.Idle:
      return null;

    case RecordReviewDisplayState.UploadingEmpty:
    case RecordReviewDisplayState.UploadingReady: {
      const prompt =
        context.evaluationType === EvaluationTypeEnum.Supplemental
          ? SUPPLEMENTAL_UPLOAD
          : COMPREHENSIVE_UPLOAD;
      return <UploadingRecordsState context={context} prompt={prompt} />;
    }

    case RecordReviewDisplayState.UploadingFollowUp:
    case RecordReviewDisplayState.MergingFollowUp:
      return <FollowUpState context={context} />;

    case RecordReviewDisplayState.Processing:
      return <ProcessingRecordsView context={context} />;

    case RecordReviewDisplayState.Complete:
    case RecordReviewDisplayState.CompleteWithFailedRecord:
    case RecordReviewDisplayState.CompleteWithFollowUp:
    case RecordReviewDisplayState.Failed:
      return <CompletedRecordsView context={context} />;
  }
};

const FollowUpState: FC<{
  context: ProcessRecordsStateContext;
}> = ({ context }) => {
  const {
    id,
    projectID,
    variant,
    canChange,
    evaluationType,
    recordReview,
    priorInputKind,
    onChangeEvalType,
    onUploadRecords,
    onUploadPriorRecords,
    onUploadPriorReview,
    onPriorInputKindChange,
  } = context;

  // Routing into FollowUpState guarantees evaluationType === Followup; the
  // ?? fallback is defensive so the pill is total even if context drift
  // ever lets a non-Followup project reach this branch.
  const followUpEvalType = evaluationType ?? EvaluationTypeEnum.Followup;

  const body = (
    <FollowUpUploadStage
      projectID={projectID}
      priorReviewDoc={recordReview?.priorReviewDocument ?? null}
      baselineSourceDocs={recordReview?.baselineSourceDocuments ?? []}
      additionalSourceDocs={recordReview?.additionalSourceDocuments ?? []}
      priorInputKind={priorInputKind}
      onPriorInputKindChange={onPriorInputKindChange}
      onUploadPriorReview={onUploadPriorReview}
      onUploadPriorRecords={onUploadPriorRecords}
      onUploadSourceDocument={onUploadRecords}
    />
  );

  // Per D10: once both zones have files (C1-c / PL-5) the chrome flips from
  // the upload prompt to the active processing bar. Before then, the
  // pre-upload indigo chrome stays.
  if (hasBothFollowUpZones(recordReview)) {
    return (
      <RecordsStatusBar
        id={id}
        variant="active"
        icon={<RsbSpinnerIcon />}
        title={
          recordReview?.title ||
          "Processing prior records and comparing new records..."
        }
        subtitle={recordReview?.subtitle}
        badgeStack={<EvalTypeBadge type={followUpEvalType} />}
        body={body}
        progress={followUpProgressPercent(recordReview)}
      />
    );
  }

  return (
    <RecordsStatusBar
      id={id}
      variant={variant}
      icon={<RsbUploadIcon />}
      title="Upload prior records and new records"
      subtitle="We'll compare records and flag what was previously reviewed"
      badgeStack={<EvalTypeBadge type={followUpEvalType} />}
      actions={<RsbChangeLink locked={!canChange} onClick={onChangeEvalType} />}
      body={body}
    />
  );
};

const UploadingRecordsState: FC<{
  context: ProcessRecordsStateContext;
  prompt: UploadPrompt;
}> = ({ context, prompt }) => {
  const { id, projectID, variant, canChange, sourceFiles } = context;
  return (
    <FileManager save={context.onUploadRecords}>
      <RecordsStatusBar
        id={id}
        variant={variant}
        icon={<RsbUploadIcon />}
        title={prompt.title}
        subtitle={prompt.subtitle}
        badgeStack={<EvalTypeBadge type={prompt.evalType} />}
        actions={
          <RsbChangeLink
            locked={!canChange}
            onClick={context.onChangeEvalType}
          />
        }
        body={
          <div>
            <RecordsUploadZone />
            <FileQueue render={renderFileQueueItem} />
            {sourceFiles.length > 0 && (
              <div className="mt-3">
                <RecordFilePillStack
                  docs={[...sourceFiles]}
                  projectID={projectID}
                  tone="upload"
                />
              </div>
            )}
          </div>
        }
      />
    </FileManager>
  );
};
